/**
 * LCVFR Artifact Store — UI state for workflow artifacts.
 *
 * Prime directive: "UI renders from artifacts, not events."
 * This store keeps the latest artifact per (encounterId, artifactType) pair,
 * using the monotonically increasing `sequence` field to determine freshness.
 *
 * @module stores/lcvfr-store
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { ArtifactType, ArtifactId, EncounterId, RunId, LcvfrRunStatus } from '@assistmd/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** A single LCVFR artifact as received from the server. */
export interface LcvfrArtifactEntry {
  readonly id: ArtifactId;
  readonly runId: RunId;
  readonly encounterId: EncounterId;
  readonly nodeId: string | null;
  readonly type: ArtifactType;
  readonly sequence: number;
  readonly contentText: string | null;
  readonly contentJson: Record<string, unknown> | null;
  readonly metaJson: Record<string, unknown> | null;
  readonly createdAt: string;
}

/** Summary of a workflow run visible to the UI. */
export interface LcvfrRunSummary {
  readonly runId: RunId;
  readonly status: LcvfrRunStatus;
  readonly nodeCount: number;
  readonly artifactsWritten: number;
  readonly totalDurationMs: number;
  readonly error: string | null;
  readonly finishedAt: string;
}

export interface LcvfrState {
  /**
   * Latest artifact per type for the active encounter.
   * Key = ArtifactType, Value = highest-sequence artifact.
   */
  latestArtifacts: Partial<Record<ArtifactType, LcvfrArtifactEntry>>;

  /** Active encounter ID the store is tracking. */
  activeEncounterId: EncounterId | null;

  /** Recent run summaries for the active encounter (most recent first). */
  recentRuns: LcvfrRunSummary[];

  /** Whether the LCVFR pipeline is currently processing. */
  pipelineActive: boolean;
}

interface LcvfrActions {
  /** Upsert an artifact — only replaces if sequence is higher. */
  upsertArtifact: (artifact: LcvfrArtifactEntry) => void;

  /** Set the active encounter and clear stale state. */
  setActiveEncounter: (encounterId: EncounterId) => void;

  /** Record a run completion for the run console. */
  addRunSummary: (summary: LcvfrRunSummary) => void;

  /** Set pipeline active state. */
  setPipelineActive: (active: boolean) => void;

  /** Clear all LCVFR state. */
  clear: () => void;
}

type LcvfrStore = LcvfrState & LcvfrActions;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_RECENT_RUNS = 50;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialState: LcvfrState = {
  latestArtifacts: {},
  activeEncounterId: null,
  recentRuns: [],
  pipelineActive: false,
};

export const useLcvfrStore = create<LcvfrStore>()(
  immer((set) => ({
    ...initialState,

    upsertArtifact: (artifact) => {
      set((state) => {
        // Only accept artifacts for the active encounter
        if (state.activeEncounterId && artifact.encounterId !== state.activeEncounterId) {
          return;
        }

        const existing = state.latestArtifacts[artifact.type];
        // Only replace if new sequence is strictly higher
        if (!existing || artifact.sequence > existing.sequence) {
          state.latestArtifacts[artifact.type] = artifact;
        }
      });
    },

    setActiveEncounter: (encounterId) => {
      set((state) => {
        if (state.activeEncounterId !== encounterId) {
          // New encounter — clear stale artifacts and runs
          state.activeEncounterId = encounterId;
          state.latestArtifacts = {};
          state.recentRuns = [];
          state.pipelineActive = false;
        }
      });
    },

    addRunSummary: (summary) => {
      set((state) => {
        state.recentRuns.unshift(summary);
        if (state.recentRuns.length > MAX_RECENT_RUNS) {
          state.recentRuns = state.recentRuns.slice(0, MAX_RECENT_RUNS);
        }
      });
    },

    setPipelineActive: (active) => {
      set((state) => {
        state.pipelineActive = active;
      });
    },

    clear: () => {
      set(() => ({ ...initialState }));
    },
  })),
);
